import {
  STEPS,
  STEPS_CONST,
  setScheduleTourStep,
  selectScheduleTourStep,
  selectScheduleTourDate,
  selectScheduleTourPhone,
  setScheduleTourShowModal,
  setScheduleTourQuestionsText,
  selectScheduleTourIsInPerson,
  selectScheduleTourQuestionsText,
  setScheduleTourStepAdvanceOneStep,
  selectScheduleTourPreferredAgent,
  STEPS_REA
} from '../../../../../../../../store/slices/scheduleTour'
import {
  selectCustomerType,
  selectCustomerIsRealEstateAgent
} from '../../../../../../../../store/selectors/customerProfile'
import * as Sentry from '@sentry/browser'
import TextArea from 'react-textarea-autosize'
import Step from '../../../../../ModalStepsOutline'
import Text from '../../../../../../../shared/Text'
import { useDispatch, useSelector } from 'react-redux'
import { ChangeEvent, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { CustomerType } from '../../../../../../../../types/customerType'
import { IconExclamationTriangle } from '../../../../../../../shared/Icon'
import { TrackingEventConstants } from '../../../../../../../../constants/tracking'
import { TrackingEventHelpers } from '../../../../../../../../helpers/tracking'
import { Listing, MessageTopicKey } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'
import { selectClientFirstName } from '../../../../../../../../store/selectors/client'
import { useTrackingContext } from '../../../../../../../../providers/tracking'
import { useSendListingMessage } from '../../../../../../../../hooks/listings/useSendListingMessage'

import styles from './Questions.module.scss'
import { useScheduleTourStep } from '../../../../../../../../hooks/useScheduleTourStep'

export interface QuestionsProps {
  listing: Listing
}

export const Questions = ({ listing }: QuestionsProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [isApiError, setIsApiError] = useState(false)
  const currentStep = useSelector(selectScheduleTourStep)
  const questionsText = useSelector(selectScheduleTourQuestionsText)
  const stepIndex = STEPS.indexOf(currentStep)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)

  const { scheduleTour, isLoading } = useScheduleTourStep({ listing })
  const message = defineMessages({
    placeholder: {
      id: 'ScheduleTour.Questions.Placeholder',
      defaultMessage: 'For example, we are available next week and are usually available after 6pm most days.'
    },
    consent: {
      id: 'ScheduleTour.Questions.Consent',
      defaultMessage: `By clicking 'Send a Message' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your
            contact information (name and email address) and any other information you include in your question with the
            real estate professional or entity selected above. You agree that the selected real estate professional or
            entity may contact you about your inquiry.`
    }
  })

  async function handleScheduleTourButtonClick() {
    try {
      await scheduleTour(questionsText)
      dispatch(setScheduleTourStepAdvanceOneStep())
    } catch (e) {
      setIsApiError(true)
    }
  }

  return (
    <Step stepLabel='schedule-tour-questions'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={isCustomerREA ? STEPS_REA : STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() =>
          isCustomerREA
            ? dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 2]))
            : dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))
        }
      >
        <FormattedMessage id='ScheduleTour.stepProgressCounter.lastStep' defaultMessage='Last step' />
      </Step.HeaderNavSection>
      <Step.Title labelFor='contact-step-questions'>
        <FormattedMessage
          id='ScheduleTour.Questions.Label'
          defaultMessage='Do you have anything you would like to add to your request?'
        />
      </Step.Title>
      <Step.Body>
        <div className={styles.bodyContents}>
          <TextArea
            id='contact-step-questions'
            value={questionsText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              if (e.target.value.length < 3000) {
                dispatch(setScheduleTourQuestionsText(e.target.value))
              }
            }}
            autoFocus
            minRows={6}
            maxRows={11}
            className={styles.textarea}
            placeholder={intl.formatMessage(message.placeholder)}
          />
          <Text size={'xxs'} align='left'>
            {intl.formatMessage(message.consent)}
          </Text>

          {isApiError ? (
            <div>
              <div>
                <IconExclamationTriangle className={styles.triangle} />
              </div>
              <Text
                className={styles.typicalResponseTimeText}
                variant='warning'
                variantCount='600'
                align='center'
                element='h3'
                weight='semibold'
                size='xxs'
              >
                <FormattedMessage
                  id='ScheduleTour.Questions.ApiError'
                  defaultMessage="Sorry about this, but something didn't work on our end. Can you try again?"
                />
              </Text>
            </div>
          ) : (
            <Text
              className={styles.typicalResponseTimeText}
              variant='neutralCool'
              variantCount='900'
              align='center'
              element='h3'
              weight='semibold'
              size='xxs'
            >
              <FormattedMessage
                id='ScheduleTour.Questions.TypicalResponseTime'
                defaultMessage='Typical response time is 24 to 48 hours'
              />
            </Text>
          )}
        </div>
      </Step.Body>
      <Step.FooterButton onClick={() => handleScheduleTourButtonClick()} loading={isLoading}>
        <FormattedMessage id='ScheduleTour.FooterButton.RequestTour' defaultMessage='Request Tour' />
      </Step.FooterButton>
    </Step>
  )
}
